import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Todo } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface TodoState {
  todos: Todo[];
  todo: Todo | null;
  loading: boolean;
  error: string | null;
}

const initialState: TodoState = {
  todos: [],
  todo: null,
  loading: false,
  error: null,
};

export const fetchTodos = createAsyncThunk<Todo[], void, { state: RootState }>(
  'todo/fetchTodos',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/todos/all/${authentification.user.OwnerId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch todos');
    }
  }
);

export const fetchTodo = createAsyncThunk<Todo, number, { state: RootState }>(
  'todo/fetchTodo',
  async (todoId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      // ✅ CORRECTION: todoId sans quotes
      const response = await axios.get(`${baseUrlApi}/todos/${todoId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchTodos());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch todo');
    }
  }
);

export const createTodo = createAsyncThunk<Todo, Partial<Todo>, { state: RootState }>(
  'todo/createTodo',
  async (todoData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.post(`${baseUrlApi}/todos/`, todoData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchTodos());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create todo');
    }
  }
);

export const updateTodo = createAsyncThunk<
  Todo,
  { todoData: Partial<Todo>; todoId: number },
  { state: RootState }
>(
  'todo/updateTodo',
  async ({ todoData, todoId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      // ✅ CORRECTION: todoId sans quotes
      const response = await axios.put(`${baseUrlApi}/todos/${todoId}`, todoData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchTodos());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update todo');
    }
  }
);

export const deleteTodo = createAsyncThunk<number, number, { state: RootState }>(
  'todo/deleteTodo',
  async (todoId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      // ✅ CORRECTION: todoId sans quotes
      await axios.delete(`${baseUrlApi}/todos/${todoId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchTodos());
      return todoId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete todo');
    }
  }
);

const todoSlice = createSlice({
  name: 'todo',
  initialState,
  reducers: {
    clearTodo: (state) => { state.todo = null; },
    clearTodos: (state) => { state.todos = []; },
    resetTodoState: () => initialState,
  },
  extraReducers: (builder) => {
    [fetchTodos, fetchTodo, createTodo, updateTodo, deleteTodo].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    builder
      .addCase(fetchTodos.fulfilled, (state, action) => {
        state.loading = false;
        state.todos = action.payload;
      })
      .addCase(fetchTodo.fulfilled, (state, action) => {
        state.loading = false;
        state.todo = action.payload;
      })
      .addCase(createTodo.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateTodo.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteTodo.fulfilled, (state, action) => {
        state.loading = false;
        state.todos = state.todos.filter(item => item.id !== action.payload);
      });
  },
});

export const { clearTodo, clearTodos, resetTodoState } = todoSlice.actions;
export default todoSlice.reducer;